import { Component, Inject, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { IonApp, IonRouterOutlet, Platform } from '@ionic/angular/standalone';

import { App, URLOpenListenerEvent } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

import { AnalyticsService } from '@guardfinder/analytics';
import {
    AnalyticsConsentModalComponent,
    AuthService,
    ClientUserService,
    EnvironmentInterface,
    iconFileNamePathsObject,
    isDevelopmentEnvironment,
    NetworkService,
    NotificationsService,
    SvgIconService,
    UserClientInterface
} from '@guardfinder/shared';

@Component({
    selector: 'gf-client-root',
    templateUrl: 'app.component.html',
    standalone: true,
    imports: [IonApp, IonRouterOutlet, AnalyticsConsentModalComponent]
})
export class AppComponent implements OnDestroy {
    webappUrl: string = '';
    protected readonly isDevEnvironment: boolean;

    constructor(
        svgIconService: SvgIconService,
        private platform: Platform,
        private router: Router,
        private authService: AuthService,
        private nService: NotificationsService,
        private clientUserService: ClientUserService,
        private analytics: AnalyticsService,
        private networkService: NetworkService,
        private zone: NgZone,
        @Inject('environment') environment: EnvironmentInterface
    ) {
        this.webappUrl = environment.webappUrl.replace('https://', '');

        this.isDevEnvironment = isDevelopmentEnvironment(environment);

        svgIconService.setIconAssetPath('/assets/svg-icons/');
        svgIconService.setIconFileNamesObject(iconFileNamePathsObject);
        svgIconService.setUseIonIconComponent(true);

        this.initializeApp();
    }

    ngOnDestroy(): void {
        if (this.platform.is('hybrid')) {
            this.networkService.capacitorNetworkWatchStop();
        } else {
            this.networkService.browserNetworkWatchStop();
        }
    }

    initUserData(user: UserClientInterface | null) {
        if (!user) return;

        this.nService.startNotificationSubscription();

        this.clientUserService.setUser(user);
    }

    initPlatform() {
        if (this.platform.is('hybrid')) {
            StatusBar.setStyle({ style: Style.Light });

            if (this.platform.is('android')) {
                StatusBar.setBackgroundColor({ color: '#FFFFFF' });
            }

            this.networkService.capacitorNetworkWatchInit();
        } else {
            this.networkService.browserNetworkWatchInit();
        }
    }

    async handleRedirects() {
        const redirect = this.authService.getRedirectRouterUrl();

        if (redirect !== '') {
            this.router.navigateByUrl(redirect).then();
        }
    }

    initializeApp() {
        this.platform.ready().then(() => {
            this.analytics
                .logAppLaunch()
                .then()
                .catch(err => console.error('Failed to log app launch:', err));

            this.initUserData(this.clientUserService.user());

            this.initPlatform();

            SplashScreen.hide();
            this.networkService.checkConnection();

            if (this.platform.is('hybrid')) {
                App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
                    console.log('appurlopen');
                    this.zone.run(() => {
                        // Example url: https://beerswift.app/tabs/tab2
                        // slug = /tabs/tab2
                        const slug = event.url.split(this.webappUrl).pop();
                        if (slug) {
                            this.router.navigateByUrl(slug);
                        } else {
                            // If no match, do nothing - let regular routing
                            // logic take over
                            this.handleRedirects();
                        }
                    });
                });
            } else {
                this.handleRedirects();
            }
        });
    }
}
